import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Users,
  UserCheck,
  UserPlus,
  DollarSign,
  Search,
  FileSpreadsheet,
  ChevronRight,
  Eye
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatDate, formatPrice } from '../../utils/format';
import { fetchWithAuth } from '../../hooks/useAuth';

interface CustomerStats {
  totalOrders: number;
  totalSpent: number;
  lastOrderDate: string | null;
}

interface Customer {
  id: number;
  email: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  stats: CustomerStats;
}

interface PaginationInfo {
  total: number;
  pages: number;
  currentPage: number;
  perPage: number;
}

export function CustomersPage() {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');

  // Fetch customers with pagination
  const { data, isLoading } = useQuery({
    queryKey: ['customers', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: '10',
        ...(search && { search }),
      });

      const response = await fetchWithAuth(`/api/admin/customers?${params}`);
      if (!response.ok) throw new Error('Eroare la încărcarea clienților');
      return response.json() as Promise<{
        status: string;
        date: {
          customers: Customer[];
          pagination: PaginationInfo;
        };
      }>;
    },
  });

  // Fetch customer statistics
  const { data: stats } = useQuery({
    queryKey: ['customer-stats'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/customers/stats');
      if (!response.ok) throw new Error('Eroare la încărcarea statisticilor');
      return response.json() as Promise<{
        totalCustomers: number;
        activeCustomers: number;
        newThisMonth: number;
        averageOrderValue: number;
      }>;
    },
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Users className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clienți</h1>
          </div>
        </div>
        <Button 
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          onClick={async () => {
            try {
              const response = await fetchWithAuth('/api/admin/customers/export', {
                method: 'GET',
                headers: {
                  'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
              });
              if (!response.ok) {
                throw new Error('Eroare la exportul clienților');
              }
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `clienti-${formatDate(new Date())}.xlsx`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch (error) {
              console.error('Export error:', error);
            }
          }}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Export Excel
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Users className="w-4 h-4" />
              Total Clienți
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.totalCustomers || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <UserCheck className="w-4 h-4" />
              Clienți Activi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.activeCustomers || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <UserPlus className="w-4 h-4" />
              Clienți Noi Luna Aceasta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {stats?.newThisMonth || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <DollarSign className="w-4 h-4" />
              Valoare Medie Comandă
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(stats?.averageOrderValue || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
          <Input
            className="pl-9"
            placeholder="Caută după nume, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      {/* Customers Table */}
      <Card className="bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Client</TableHead>
                <TableHead className="font-semibold">Data Înregistrării</TableHead>
                <TableHead className="font-semibold">Total Comenzi</TableHead>
                <TableHead className="font-semibold">Total Cheltuit</TableHead>
                <TableHead className="font-semibold">Ultima Comandă</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <div className="flex items-center justify-center text-gray-500">
                      Se încarcă...
                    </div>
                  </TableCell>
                </TableRow>
              ) : data?.date.customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <div className="text-gray-500">Nu există clienți</div>
                  </TableCell>
                </TableRow>
              ) : (
                data?.date.customers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{customer.name || 'N/A'}</span>
                        <span className="text-sm text-gray-500">
                          {customer.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(customer.createdAt)}</TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-800 ring-1 ring-blue-200">
                        {customer.stats.totalOrders} comenzi
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatPrice(customer.stats.totalSpent)}
                    </TableCell>
                    <TableCell>
                      {customer.stats.lastOrderDate 
                        ? formatDate(customer.stats.lastOrderDate)
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-900"
                        asChild
                      >
                        <a href={`/admin/customers/${customer.id}`}>
                          <Eye className="w-4 h-4" />
                          Detalii
                          <ChevronRight className="w-4 h-4" />
                        </a>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {data?.date.pagination && data.date.pagination.total > 10 && (
          <div className="flex justify-center mt-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-4"
              >
                Anterior
              </Button>
              <span className="text-sm text-gray-600">
                Pagina {page} din {data.date.pagination.pages}
              </span>
              <Button
                variant="outline"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= data.date.pagination.pages}
                className="px-4"
              >
                Următor
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
